import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DEFAULT_REGIONS, RegionPicker } from "@/components/llm_network/EnvRegionsCard";

const SELECT = "rounded border border-white/10 bg-black px-2 py-1 font-mono text-white disabled:opacity-40";
const CHANNELS = ["dev", "stage", "prod"];

/** Hub → Environments: channel and region of one spoke — changed in place (super admin). */
export function EnvPlacementCell({ env, canWrite, onChanged }) {
    const [region, setRegion] = useState(undefined);
    const [busy, setBusy] = useState(false);
    const id = env.env_id;

    const run = async (fn, ok) => {
        setBusy(true);
        try { await fn(); toast.success(ok); onChanged?.(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
    };
    const saveChannel = (channel) => channel !== env.channel &&
        run(() => api.put(`/admin/sync/environments/${id}/channel`, { channel }), `${env.name} moved to ${channel}`);
    const saveRegion = () => run(async () => {
        await api.put(`/admin/llm-network/environments/${id}/region`, { region: region ?? "" });
        setRegion(undefined);
    }, region ? `${env.name} set to region ${region}` : `${env.name}: region tag cleared`);

    if (!canWrite) {
        return <div className="font-mono text-white/80" data-testid={`env-channel-${id}`}>{env.channel}<span className="text-white/40"> · {env.region || "no region"}</span></div>;
    }
    return (
        <div className="space-y-1.5" data-testid={`env-placement-${id}`}>
            <select value={env.channel} disabled={busy} onChange={(e) => saveChannel(e.target.value)} className={SELECT}
                    aria-label={`Channel of ${env.name}`} data-testid={`env-channel-${id}`}>
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex flex-wrap items-center gap-1.5">
                <RegionPicker key={env.region || "unset"} value={region ?? env.region ?? ""} known={DEFAULT_REGIONS} disabled={busy}
                              onChange={setRegion} testId={`env-region-${id}`} />
                {region !== undefined && region !== (env.region || "") && (
                    <button onClick={saveRegion} disabled={busy} className="rounded border border-white/10 px-2 py-1 text-white/70 hover:bg-white/5 disabled:opacity-40"
                            data-testid={`env-region-save-${id}`}>Save</button>
                )}
            </div>
            <div className="text-[10px] text-white/40" data-testid={`env-region-source-${id}`}>
                {env.region ? (env.region_source === "hub" ? "region set on hub" : "region reported by spoke") : "no region tag"}
                {env.region && env.reported_region && env.region !== env.reported_region && (
                    <span className="inline-flex items-center gap-1 ml-1 text-amber-200"><AlertTriangle className="w-3 h-3" /> spoke reports {env.reported_region}</span>
                )}
            </div>
        </div>
    );
}
